import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './YouTubePopular.css';

const YouTubePopular = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [region, setRegion] = useState('KR');
  const [category, setCategory] = useState('');
  const [count, setCount] = useState('150');
  const [includeChannelInfo, setIncludeChannelInfo] = useState(true);
  
  // API URL 환경 변수에서 가져오기
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  const [stats, setStats] = useState({
    totalVideos: 0,
    totalViews: 0,
    avgViews: 0
  });
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    loadVideos();
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    } else if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toString() || '0';
  };

  const formatDuration = (duration) => {
    if (!duration) return '0:00';
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    if (!match) return '0:00';
    
    const hours = (match[1] || '').replace('H', '');
    const minutes = (match[2] || '').replace('M', '');
    const seconds = (match[3] || '').replace('S', '');
    
    const parts = [];
    if (hours) parts.push(hours + ':');
    parts.push((minutes || '0').padStart(hours ? 2 : 1, '0') + ':');
    parts.push((seconds || '0').padStart(2, '0'));
    
    return parts.join('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    
    if (years > 0) return `${years}년 전`;
    if (months > 0) return `${months}개월 전`;
    if (days > 0) return `${days}일 전`;
    if (hours > 0) return `${hours}시간 전`;
    if (minutes > 0) return `${minutes}분 전`;
    return '방금 전';
  };

  const loadVideos = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        region: region,
        count: count,
        include_channel_info: includeChannelInfo
      });
      if (category) params.append('category', category);

      const response = await fetch(`${API_BASE_URL}/api/youtube/popular-videos?${params}`);
      const data = await response.json();

      if (data.success) {
        setVideos(data.videos);
        updateStats(data.videos);
      } else {
        throw new Error(data.error || 'API 요청 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMultiCategory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/youtube/popular-by-categories?region=${region}`);
      const data = await response.json();

      if (data.success) {
        setVideos(data.videos);
        updateStats(data.videos);
      } else {
        throw new Error(data.error || 'API 요청 실패');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (videoList) => {
    const totalViews = videoList.reduce((sum, v) => sum + (v.viewCount || 0), 0);
    const avgViews = videoList.length > 0 ? Math.round(totalViews / videoList.length) : 0;
    
    setStats({
      totalVideos: videoList.length,
      totalViews: totalViews,
      avgViews: avgViews
    });
  };

  const sortVideos = (field) => {
    let newOrder = 'desc';
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    
    setSortField(field);
    setSortOrder(newOrder);

    const sorted = [...videos].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];

      if (aVal === null || aVal === undefined) aVal = 0;
      if (bVal === null || bVal === undefined) bVal = 0;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (field === 'channelCreatedAt' || field === 'publishedAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (newOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    setVideos(sorted);
  };

  const getRankDisplay = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return rank;
  };

  const getRankClass = (rank) => {
    if (rank === 1) return 'top1';
    if (rank === 2) return 'top2';
    if (rank === 3) return 'top3';
    if (rank <= 10) return 'top10';
    return '';
  };

  return (
    <div className="youtube-popular">
      <div className="youtube-header">
        <h1>🔥 YouTube 인기 동영상 TOP 150</h1>
        <p>실시간 YouTube API로 가져온 인기 동영상</p>
      </div>

      <div className="youtube-stats">
        <div className="stat-item">
          <div className="stat-value">{stats.totalVideos}</div>
          <div className="stat-label">총 동영상</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{formatNumber(stats.totalViews)}</div>
          <div className="stat-label">총 조회수</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{formatNumber(stats.avgViews)}</div>
          <div className="stat-label">평균 조회수</div>
        </div>
      </div>

      <div className="youtube-controls">
        <div className="control-group">
          <label>지역:</label>
          <select value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="KR">🇰🇷 한국</option>
            <option value="US">🇺🇸 미국</option>
            <option value="JP">🇯🇵 일본</option>
            <option value="GB">🇬🇧 영국</option>
            <option value="IN">🇮🇳 인도</option>
          </select>
        </div>

        <div className="control-group">
          <label>카테고리:</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">전체</option>
            <option value="10">🎵 음악</option>
            <option value="20">🎮 게임</option>
            <option value="24">🎬 엔터테인먼트</option>
            <option value="17">⚽ 스포츠</option>
            <option value="23">😂 코미디</option>
            <option value="28">🔬 과학/기술</option>
            <option value="22">👥 인물/블로그</option>
            <option value="26">💄 하우투/스타일</option>
          </select>
        </div>

        <div className="control-group">
          <label>개수:</label>
          <select value={count} onChange={(e) => setCount(e.target.value)}>
            <option value="50">50개</option>
            <option value="100">100개</option>
            <option value="150">150개</option>
            <option value="200">200개 (최대)</option>
          </select>
        </div>

        <div className="control-group">
          <input 
            type="checkbox" 
            id="includeChannelInfo" 
            checked={includeChannelInfo}
            onChange={(e) => setIncludeChannelInfo(e.target.checked)}
          />
          <label htmlFor="includeChannelInfo">채널 정보 포함</label>
        </div>

        <button onClick={loadVideos} disabled={loading}>
          🔄 불러오기
        </button>
        <button onClick={loadMultiCategory} disabled={loading}>
          📊 카테고리별 150개
        </button>
      </div>

      <div className="youtube-content">
        {loading && (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>YouTube API에서 데이터를 가져오는 중...</p>
            {includeChannelInfo && <p>채널 정보도 함께 가져오는 중... (시간이 조금 더 걸립니다)</p>}
          </div>
        )}

        {error && (
          <div className="error">
            <h3>오류 발생</h3>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && videos.length > 0 && (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>순위</th>
                  <th>미리보기</th>
                  <th className="sortable" onClick={() => sortVideos('title')}>
                    제목 / 채널 {sortField === 'title' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  {videos[0]?.subscriberCount !== undefined && (
                    <>
                      <th className="sortable" onClick={() => sortVideos('subscriberCount')}>
                        구독자 {sortField === 'subscriberCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="sortable" onClick={() => sortVideos('channelViewCount')}>
                        채널 총 조회수 {sortField === 'channelViewCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="sortable" onClick={() => sortVideos('channelVideoCount')}>
                        채널 영상수 {sortField === 'channelVideoCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </th>
                    </>
                  )}
                  <th>길이</th>
                  <th className="sortable" onClick={() => sortVideos('viewCount')}>
                    조회수 {sortField === 'viewCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => sortVideos('likeCount')}>
                    좋아요 {sortField === 'likeCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => sortVideos('channelCreatedAt')}>
                    채널 생성일 {sortField === 'channelCreatedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="sortable" onClick={() => sortVideos('commentCount')}>
                    댓글 {sortField === 'commentCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  {videos[0]?.category && <th>카테고리</th>}
                </tr>
              </thead>
              <tbody>
                {videos.map((video, index) => {
                  const rank = index + 1;
                  return (
                    <tr key={video.id} onClick={() => {
                      // localStorage에 선택한 비디오 정보 저장
                      localStorage.setItem('selectedYouTubeVideo', JSON.stringify(video));
                      
                      // 커스텀 이벤트 발생 (같은 창 내에서 감지용)
                      window.dispatchEvent(new Event('youtubeVideoSelected'));
                      
                      // iframe 내에서 실행 중인지 확인
                      const isInIframe = window.self !== window.top;
                      
                      if (isInIframe) {
                        // iframe 내에서 실행 중인 경우 (Editor 페이지의 YouTube 탭)
                        window.parent.postMessage({
                          type: 'SELECT_YOUTUBE_VIDEO',
                          video: video
                        }, '*');
                      } else {
                        // 독립 페이지에서 실행 중인 경우
                        const lastProjectId = localStorage.getItem('lastProjectId');
                        if (lastProjectId) {
                          // 프로젝트가 있으면 에디터의 벤치마킹 탭으로 이동
                          window.location.href = `/editor/${lastProjectId}?tab=benchmark&videoId=${video.id}`;
                        } else {
                          // 프로젝트가 없으면 기본 프로젝트 ID 사용하거나 새로 생성
                          alert('프로젝트를 먼저 선택하거나 생성해주세요.');
                          // 또는 기본 프로젝트로 이동
                          // window.location.href = `/editor/new?tab=benchmark&videoId=${video.id}`;
                        }
                      }
                    }}>
                      <td className={`rank-cell ${getRankClass(rank)}`}>
                        {getRankDisplay(rank)}
                      </td>
                      <td className="thumbnail-cell">
                        <img src={video.thumbnail} alt={video.title} className="small-thumbnail" />
                      </td>
                      <td className="title-cell">
                        <div className="video-title-table" title={video.title}>{video.title}</div>
                        <div className="channel-name">
                          {video.channelThumbnail && (
                            <img src={video.channelThumbnail} className="channel-thumb" alt="" />
                          )}
                          {video.channel}
                        </div>
                      </td>
                      {video.subscriberCount !== undefined && (
                        <>
                          <td className="stats-cell">
                            <div className="subscriber-count">{formatNumber(video.subscriberCount)}</div>
                          </td>
                          <td className="stats-cell">
                            <div className="channel-views">{formatNumber(video.channelViewCount)}</div>
                          </td>
                          <td className="stats-cell">
                            <div className="channel-videos">{formatNumber(video.channelVideoCount)}</div>
                          </td>
                        </>
                      )}
                      <td className="duration-cell">{formatDuration(video.duration)}</td>
                      <td className="stats-cell">
                        <div className="view-count">{formatNumber(video.viewCount)}</div>
                      </td>
                      <td className="stats-cell">
                        <div className="like-count">👍 {formatNumber(video.likeCount)}</div>
                      </td>
                      <td className="date-cell">
                        {formatDate(video.channelCreatedAt)}
                      </td>
                      <td className="stats-cell">
                        💬 {formatNumber(video.commentCount)}
                      </td>
                      {video.category && (
                        <td><span className="category-badge">{video.category}</span></td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default YouTubePopular;